import axios from 'axios';
import axiosInstance from './axios-interceptor';


const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;
const TIME_SERIES_PREFETCH_CACHE_VERSION = 'v2';

const getRangeAwareTimeoutMs = (startDate?: string, endDate?: string): number => {
    if (!startDate || !endDate) return 60_000;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 60_000;
    const diffMs = Math.abs(end.getTime() - start.getTime());
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays >= 90) return 180_000; // 3 minutes for long-range requests
    if (diffDays >= 31) return 120_000; // 2 minutes for medium-range requests
    return 60_000;
};

const normalizeAggregationParam = (aggregation?: string): string | undefined => {
    if (!aggregation) return aggregation;
    if (aggregation === 'daily') return '1D';
    if (aggregation === 'hourly') return '1H';
    return aggregation;
};

const getRangeDays = (startDate?: string, endDate?: string): number => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    return Math.floor(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
};

const is404 = (error: any): boolean => {
    const status = error?.response?.status;
    return Number(status) === 404;
};

const getWithSlashFallback = async (urlWithSlash: string, config: any) => {
    try {
        return await axiosInstance.get(urlWithSlash, config);
    } catch (error) {
        if (!is404(error) || !urlWithSlash.endsWith('/')) throw error;
        const urlWithoutSlash = urlWithSlash.replace(/\/$/, '');
        return axiosInstance.get(urlWithoutSlash, config);
    }
};

const mergeChunkedTimeSeries = (responses: any[], stationId: string, variable: string, startDate: string, endDate: string, aggregation?: string) => {
    const mergedByTs = new Map<string, any>();
    responses.forEach((res) => {
        const items = Array.isArray(res?.data) ? res.data : [];
        items.forEach((item: any) => {
            const ts = item?.timestamp;
            if (!ts) return;
            mergedByTs.set(ts, item);
        });
    });

    const mergedData = Array.from(mergedByTs.values()).sort((a: any, b: any) => {
        const ta = new Date(a.timestamp).getTime();
        const tb = new Date(b.timestamp).getTime();
        return ta - tb;
    });

    const firstMeta = responses.find((r) => r?.metadata)?.metadata || {};
    return {
        data: mergedData,
        metadata: {
            ...firstMeta,
            station_id: firstMeta.station_id || stationId,
            variable: firstMeta.variable || variable,
            start_date: startDate,
            end_date: endDate,
            aggregation: firstMeta.aggregation || aggregation || 'raw',
            total_count: mergedData.length,
            count: mergedData.filter((p: any) => p?.value !== null && p?.value !== undefined).length,
        },
    };
};

export const getCurrentUser = () => {
    return axiosInstance.get(API_URL + 'auth/verify_token/');
};

export const googleLogin = (token: string) => {
    return axios.post(API_URL + 'auth/google_login/', { credential: token });
};

export const getDashboardStations = () => {
    return axiosInstance.get(API_URL + 'dashboard/stations/');
}

export const fetchStations = (
  country?: string,
  flagged?: boolean,
  station_status?: string,
) => {
  const params: any = {};
  if (country) params.country = country;
  if (flagged) params.flag = 'true';
  if (station_status) params.status = station_status;

  return axiosInstance.get(API_URL + 'stations/', {
    params,
  }).then(res => {
    const payload = res.data;
    return Array.isArray(payload) ? payload : (payload?.results ?? []);
  });
};

export const getStation = (id: string) => {
    return axiosInstance.get(API_URL + `stations/${id}/`);
}

export const getTilts = (id: string) => {
    return axiosInstance.get(API_URL + `stations/${id}/tilts/`);
}
export const getAllTickets = ( status?: string, label?: string,) => {
     const params: any = {};
      if (status) params.status = status;
      if (label) params.label = label;
    return axiosInstance.get(API_URL + 'dashboard/tickets/', {params}).then(res => res.data.tickets);
}

export const getLightening = (slug: string) => {
    return axiosInstance.get(API_URL + `stations/${slug}/lightning_data/`);
}

export const getLightningMapData = (slug: string) => {
    return axiosInstance.get(API_URL + `stations/${slug}/lightning/`);
}

export const getReports = (slug: string) => {
    return axiosInstance.get(API_URL + `stations/${slug}/reports/`);
}

export const getTickets = (slug: string) => {
    return axiosInstance.get(API_URL + `stations/${slug}/tickets/`);
}


export const getTimeSeriesData = async (
    stationId: string,
    variable: string,
    startDate: string,
    endDate: string,
     aggregation?: string,
     maxPoints?: number

) => {
       const params: any = {
        variable,
        start_date: startDate,
        end_date: endDate
    };

    // Add aggregation parameter if provided
    const normalizedAggregation = normalizeAggregationParam(aggregation);
    if (normalizedAggregation) {
        params.aggregation = normalizedAggregation;
    }
    if (maxPoints) {
        params.max_points = maxPoints;
    }

    const days = getRangeDays(startDate, endDate);
    const shouldChunk = days >= 180;

    if (shouldChunk) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const chunkDays = 60;
        const chunkResponses: any[] = [];
        let cursor = new Date(start);
        while (cursor <= end) {
            const chunkStart = new Date(cursor);
            const chunkEnd = new Date(Math.min(end.getTime(), cursor.getTime() + chunkDays * 24 * 60 * 60 * 1000));
            const chunkParams: any = {
                ...params,
                start_date: chunkStart.toISOString().slice(0, 19),
                end_date: chunkEnd.toISOString().slice(0, 19),
            };
            const chunkResponse = await getWithSlashFallback(`${API_URL}stations/${stationId}/time_series/`, {
                params: chunkParams,
                timeout: getRangeAwareTimeoutMs(chunkParams.start_date, chunkParams.end_date),
            });
            chunkResponses.push(chunkResponse.data);
            cursor = new Date(chunkEnd.getTime() + 1000);
        }
        return mergeChunkedTimeSeries(chunkResponses, stationId, variable, startDate, endDate, normalizedAggregation);
    }

    const response = await getWithSlashFallback(`${API_URL}stations/${stationId}/time_series/`, {
        params,
        timeout: getRangeAwareTimeoutMs(startDate, endDate),
    });
    return response.data;
};

export interface ChartOverlaysResponse {
    issues: any[];
    reports: any[];
    odkSubmissions: any[];
    annotationPeriods: Record<string, any[]>;
    metadata: {
        stationId: string;
        startDate: string;
        endDate: string;
    };
}

const ANNOTATION_TYPE_ALIASES: Record<string, string> = {
    dripClogPeriods: 'drip_clog_periods',
    rainfallBadLightning: 'rainfall_bad_lightning_periods',
    rainfallBadLightningPeriods: 'rainfall_bad_lightning_periods',
    rainfallBadLighting: 'rainfall_bad_lightning_periods',
    rainfall_bad_lighting_periods: 'rainfall_bad_lightning_periods',
    rainfall_bad_lightning: 'rainfall_bad_lightning_periods',
    badSatellitePeriods: 'rainfall_bad_satellite_periods',
    stormyNoRain: 'stormy_no_rain',
    severeBatteryIssues: 'severe_battery_issue',
    severe_battery_issues: 'severe_battery_issue',
    severeBatteryIssue: 'severe_battery_issue',
    imminentBatteryFailure: 'imminent_battery_failure',
    rapidBatteryDrop: 'rapid_fall',
    rapid_battery_drop: 'rapid_fall',
    offline: 'offline',
    lowCapacity: 'low_capacity',
    humidityQcSevere: 'humidity_quality_tahmo_severe',
    solarRadiationQcSevere: 'solar_radiation_quality_tahmo_severe',
};

const toCanonicalAnnotationType = (key: string): string => ANNOTATION_TYPE_ALIASES[key] || key;

const normalizeAnnotationPeriodsMap = (input: any): Record<string, any[]> => {
    if (!input || typeof input !== 'object') return {};
    const normalized: Record<string, any[]> = {};
    Object.entries(input).forEach(([key, value]) => {
        const canonicalKey = toCanonicalAnnotationType(key);
        if (Array.isArray(value)) {
            normalized[canonicalKey] = value;
            return;
        }
        if (value && typeof value === 'object') {
            // Some backends/serializers may return {0: {...}, 1: {...}} instead of arrays.
            const asArray = Object.values(value);
            normalized[canonicalKey] = asArray;
            return;
        }
        normalized[canonicalKey] = [];
    });
    return normalized;
};

export const getChartOverlays = async (
    stationId: string,
    startDate: string,
    endDate: string,
    options: {
        liveStationId?: string;
        includeIssues?: boolean;
        includeReports?: boolean;
        includeOdk?: boolean;
        annotationTypes?: string[];
    } = {}
) => {
    const params: any = {
        start_date: startDate,
        end_date: endDate,
    };
    if (options.liveStationId) params.live_station_id = options.liveStationId;
    if (options.includeIssues) params.include_issues = true;
    if (options.includeReports) params.include_reports = true;
    if (options.includeOdk) params.include_odk = true;
    if (options.annotationTypes && options.annotationTypes.length > 0) {
        params.annotation_types = options.annotationTypes.join(',');
    }
    return axiosInstance
        .get(`${API_URL}stations/${stationId}/chart_overlays/`, {
            params,
            timeout: getRangeAwareTimeoutMs(startDate, endDate),
        })
        .then(res => {
            const data = res.data || {};
            const rawAnnotationPeriods = {
                ...(data.annotationPeriods || {}),
                ...(data.annotation_periods || {}),
                ...(data.dripClogPeriods ? { dripClogPeriods: data.dripClogPeriods } : {}),
            };
            return {
                issues: data.issues || [],
                reports: data.reports || [],
                odkSubmissions: data.odkSubmissions || data.odk_submissions || data.fieldSubmissions || data.field_submissions || data.hostSubmissions || data.host_submissions || [],
                annotationPeriods: normalizeAnnotationPeriodsMap(rawAnnotationPeriods),
                metadata: {
                    stationId: data?.metadata?.stationId || data?.metadata?.station_id || stationId,
                    startDate: data?.metadata?.startDate || data?.metadata?.start_date || startDate,
                    endDate: data?.metadata?.endDate || data?.metadata?.end_date || endDate,
                },
            } as ChartOverlaysResponse;
        });
};


export const getSatellitePrecipitation = (stationId: string, startDate: string, endDate: string) => {
    return axiosInstance.get(`${API_URL}stations/${stationId}/satellite_precipitation/`, {
        params: {
            start_date: startDate,
            end_date: endDate
        }
    }).then(res => res.data);
}


export const getTahmoQCFlagged = async (stationId: string, variable: string) => {
       return axiosInstance.get(`${API_URL}stations/${stationId}/tahmo_qc_flagged/`, {
        params: {
            variable: variable
        }
    }).then(res => res.data);
};

export const getAnnotationPeriods = async (
    stationId: string,
    annotationType: string,
    startDate: string,
    endDate: string
) => {
    return axiosInstance.get(`${API_URL}stations/${stationId}/get_annotation_periods/`, {
        params: {
            annotation_type: annotationType,
            start_date: startDate,
            end_date: endDate
        }
    }).then(res => res.data);
};

export const getBatteryValue = (
  stationId: string,
  metric: 'mean' | 'min' | 'max' | 'start' | 'end' | 'delta' | 'drop_percent' = 'mean'
) => {
  return axiosInstance
    .get(`${API_URL}stations/${stationId}/battery_agg_value/`, { params: { metric } })
    .then(res => res.data.value as number | null);
};


export const getVariableValue = (
  stationId: string,
  variable: 'lb' | 'te' | 'wg' | 'ra' | 'pr' | 'rh' | 'ap' | 'ws' | 'wd',
  metric: 'mean' | 'min' | 'max' | 'start' | 'end' | 'delta' = 'mean'
) => {
  return axiosInstance
    .get(`${API_URL}stations/${stationId}/variable_agg_value/`, {
      params: { variable, metric }
    })
    .then(res => res.data.value as number | null);
}

// In-memory cache for prefetched time-series promises (keyed same as SimpleTimeSeriesChart)
const prefetchCache = new Map<string, Promise<any>>();

export const prefetchTimeSeriesData = (
    stationId: string,
    variable: string,
    startDate: string,
    endDate: string,
    aggregation = 'auto'
): void => {
    const key = `${TIME_SERIES_PREFETCH_CACHE_VERSION}|${stationId}|${variable}|${startDate}|${endDate}|${aggregation}`;
    if (prefetchCache.has(key)) return; // already in-flight or done
    // Avoid background prefetch fan-out on very large ranges.
    if (getRangeDays(startDate, endDate) >= 180) return;
    const promise = getWithSlashFallback(`${API_URL}stations/${stationId}/time_series/`, {
            params: { variable, start_date: startDate, end_date: endDate, aggregation: normalizeAggregationParam(aggregation) },
            timeout: getRangeAwareTimeoutMs(startDate, endDate),
        })
        .then(res => res.data);
    prefetchCache.set(key, promise);
    promise.finally(() => {
        // Keep entry for 5 minutes so SimpleTimeSeriesChart can benefit
        setTimeout(() => prefetchCache.delete(key), 5 * 60 * 1000);
    });
};

export const getPrefetchedTimeSeriesData = (
    stationId: string,
    variable: string,
    startDate: string,
    endDate: string,
    aggregation = 'auto'
): Promise<any> | undefined => {
    const key = `${TIME_SERIES_PREFETCH_CACHE_VERSION}|${stationId}|${variable}|${startDate}|${endDate}|${aggregation}`;
    return prefetchCache.get(key);
};

export const getBatchStationMetrics = (hours: number = 24) => {
    return axiosInstance.get(API_URL + 'dashboard/batch_station_metrics/', {
        params: { hours }
    }).then(res => {
         return res.data;
    });
};

export const getFlaggedVariables = (stationId: string, startDate?: string, endDate?: string) => {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return axiosInstance.get(API_URL + `stations/${stationId}/flagged_variables/`, { params }).then(res => res.data);
};

export const getIssuesAnnotations = async (stationId: string, startDate: string, endDate: string) => {
    return axiosInstance.get(`${API_URL}stations/${stationId}/issues_annotations/`, {
        params: {
            start_date: startDate,
            end_date: endDate
        }
    }).then(res => res.data);
};

export const getReportsWithStartEndDates = async (stationId: string, startDate: string, endDate: string, liveStationId: string) => {
    return axiosInstance.get(`${API_URL}stations/${stationId}/reports/`, {
        params: {
            start_date: startDate,
              end_date: endDate,
            live_station_id: liveStationId
        }
    }).then(res => res.data);
};

// Fetch the TAHMO variables catalog to map codes (e.g., 'ra') to human-readable names
export const getVariableCatalog = async () => {
    return axios.get('https://datahub.tahmo.org/services/assets/v2/variables').then(res => res.data);
};

export const getODKSubmissions = async (stationId: string, startDate: string, endDate: string, includeImages: boolean = false) => {
    return axiosInstance.get(`${API_URL}stations/${stationId}/host_management/`, {
        params: {
            start_date: startDate,
            end_date: endDate,
            include_images: includeImages
        },
        timeout: includeImages ? 180_000 : getRangeAwareTimeoutMs(startDate, endDate),
    }).then(res => res.data);
};

export const getColocatedStations = async (stationId: string) => {
    return axiosInstance.get(`${API_URL}stations/${stationId}/colocated_stations/`).then(res => res.data);
};

export interface UserActivityPayload {
    actionType: string;
    pagePath?: string;
    targetType?: string;
    targetLabel?: string;
    metadata?: Record<string, any>;
    sessionId?: string;
    occurredAt?: string;
}

export interface ActivityUser {
    id: number;
    name?: string;
    email: string;
}

export interface UserActivityRecord {
    id: number;
    userId: number;
    userName?: string;
    userEmail?: string;
    actionType: string;
    pagePath?: string;
    targetType?: string;
    targetLabel?: string;
    metadata?: Record<string, any>;
    sessionId?: string;
    occurredAt: string;
}

export const recordUserActivity = async (payload: UserActivityPayload) => {
    return axiosInstance.post(`${API_URL}activities/record/`, payload);
};

export const getActivityUsers = async () => {
    return axiosInstance.get(`${API_URL}activities/users/`).then(res => res.data as ActivityUser[]);
};

export const getUserActivityTimeline = async (userId?: number, limit: number = 200) => {
    const params: any = { limit };
    if (userId) params.user_id = userId;
    return axiosInstance.get(`${API_URL}activities/timeline/`, { params }).then(res => res.data as {results: UserActivityRecord[]; count: number});
};
